import { useCallback } from 'react';

import { useTooltip } from './contexts/TooltipContext.js';
import type { Data } from './types.js';
import { useTooltipReducer } from './useTooltipReducer.js';
import { emptyData } from './utils.js';
import { isTouchableDevice } from '../../utils/is-touchable-device.js';

/** @internal */
export type EventCoords = { clientX: number; clientY: number };

/**
 * Ergonomic wrapper around the low-level tooltip dispatch.
 * Encapsulates action types, coordinate rounding, and empty-data defaults so
 * callers only need to deal with intent (hover / pin / unpin / …).
 *
 * @internal
 */
export const useTooltipActions = () => {
  const dispatch = useTooltipReducer();
  const tooltipState = useTooltip();

  const hover = useCallback(
    (event: EventCoords, data?: Data) => {
      if (isTouchableDevice()) {
        return;
      }
      dispatch({
        type: 'SET_POSITION',
        payload: {
          absoluteX: Math.round(event.clientX),
          absoluteY: Math.round(event.clientY),
          data,
        },
      })();
    },
    [dispatch],
  );

  const pin = useCallback(
    (event: EventCoords, data: Data) => {
      const { absoluteX, absoluteY } = tooltipState.getState();
      const coords =
        absoluteX !== null && absoluteY !== null
          ? { clientX: absoluteX, clientY: absoluteY }
          : {
              clientX: Math.round(event.clientX),
              clientY: Math.round(event.clientY),
            };
      dispatch({
        type: 'SET_PINNED',
        payload: {
          pinned: true,
          absoluteX: coords.clientX,
          absoluteY: coords.clientY,
          data,
        },
      })();
    },
    [dispatch, tooltipState],
  );

  const unpin = useCallback(() => {
    if (isTouchableDevice()) {
      dispatch({ type: 'RESET_TOOLTIP' })();
      return;
    }
    dispatch({
      type: 'SET_PINNED',
      payload: { pinned: false, data: emptyData() },
    })();
  }, [dispatch]);

  const togglePin = useCallback(
    (event: EventCoords, data: Data) => {
      if (tooltipState.getState().pinned) {
        unpin();
      } else {
        pin(event, data);
      }
    },
    [pin, unpin, tooltipState],
  );

  const reset = useCallback(() => {
    dispatch({ type: 'RESET_TOOLTIP' })();
  }, [dispatch]);

  const outOfBounds = useCallback(() => {
    dispatch({ type: 'SET_OUT_BOUNDS' })();
  }, [dispatch]);

  return { hover, pin, togglePin, unpin, reset, outOfBounds };
};
